import { useField, useFormikContext } from 'formik';
import { useContext, useState } from 'react';
import { FilterCompletionInput, FilterCompletionOutput, FilterSection } from '../../../../../../../../typings/AICompletion.interface';
import { getAIErrorMessage } from '../../../../common/AIPromptDropdown';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
export const FILTER_SECTIONS: FilterSection[] = ['body', 'headers', 'query', 'path'];
const isJSON = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};
const GENERATION_ERROR = "Filter couldn't be generated. Try rephrasing or enter a filter manually.";

// Generates a filter rule from a prompt and applies it to the filter rule form field `name`.
const useAIFilter = (name: string) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const {
    setFieldValue
  } = useFormikContext();
  const [{
    value: rule
  }] = useField(name);
  // Incremented on every applied generation, used to remount editors with the new value.
  const [generation, setGeneration] = useState(0);

  // Resolves to the applied filters, or null if nothing was applied.
  const generateFilter = async (prompt: string, input: FilterCompletionInput['input']): Promise<FilterCompletionOutput['filters']> => {
    // Only valid JSON sections are part of the current filter, a section can hold the description.
    const current_filter = Object.fromEntries(FILTER_SECTIONS.filter(section => isJSON(rule?.[section])).map(section_0 => [section_0, rule[section_0]]));
    try {
      const {
        filters,
        explanation,
        matches_sample
      } = await HookdeckAPI.completion.completeFilter({
        prompt,
        input,
        current_filter
      });
      if (!filters) {
        addToast('error', explanation || GENERATION_ERROR);
        return null;
      }

      // Set every section in a single update, so validation runs on the complete filter.
      await setFieldValue(name, {
        ...rule,
        ...Object.fromEntries(FILTER_SECTIONS.map(section_1 => [section_1, filters[section_1] ?? '']))
      });
      setGeneration(count => count + 1);
      const sample_result = matches_sample === null ? '' : matches_sample ? ' The sample request would be delivered.' : ' The sample request would be filtered out.';
      addToast('success', `${explanation || 'Filter generated.'}${sample_result}`);
      return filters;
    } catch (error) {
      addToast('error', getAIErrorMessage(error, GENERATION_ERROR));
      return null;
    }
  };
  return {
    generateFilter,
    generation
  };
};
export default useAIFilter;